import { z } from "zod";
import type { Faq } from "../data/faq";
import {
  insuranceTypes,
  type InsuranceAffiliateStatus,
  type InsuranceAvailability,
  type InsuranceChannel,
  type InsuranceContract,
  type InsuranceCoverageItem,
  type InsuranceCoverageText,
  type InsuranceEligibility,
  type InsuranceFact,
  type InsuranceFactNotes,
  type InsurancePricing,
  type InsuranceProductKind,
  type InsuranceSource,
  type InsuranceType,
} from "../data/insurance";

export const insuranceSearchSort = ["RANGE", "A-Z", "Z-A"] as const;
export type InsuranceSearchSort = (typeof insuranceSearchSort)[number];

export const INSURANCE_DEFAULT_SORT: InsuranceSearchSort = "RANGE";
export const INSURANCE_DEFAULT_PAGE_SIZE = 24;

export const insuranceSearchParams = z.object({
  search: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().default(1).catch(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(60)
    .optional()
    .default(INSURANCE_DEFAULT_PAGE_SIZE)
    .catch(INSURANCE_DEFAULT_PAGE_SIZE),
  sort: z
    .enum(insuranceSearchSort)
    .optional()
    .default(INSURANCE_DEFAULT_SORT)
    .catch(INSURANCE_DEFAULT_SORT),
  type: z
    .enum(["ALL", ...insuranceTypes])
    .optional()
    .default("ALL")
    .catch("ALL"),
  language: z.string().optional().default("en"),
});

export type InsuranceSearchParams = z.infer<typeof insuranceSearchParams>;

export const parseInsuranceSearchParams = (
  params: unknown,
  override?: Partial<InsuranceSearchParams>,
): InsuranceSearchParams => {
  const parsed = insuranceSearchParams.safeParse(params ?? {});
  const data = parsed.success ? parsed.data : insuranceSearchParams.parse({});
  return { ...data, ...override };
};

export const getInsuranceSearchUrlQuery = (data: InsuranceSearchParams) => {
  const params = new URLSearchParams();
  if (data.search) params.append("search", data.search);
  if (data.page > 1) params.append("page", String(data.page));
  if (data.pageSize !== INSURANCE_DEFAULT_PAGE_SIZE)
    params.append("pageSize", String(data.pageSize));
  if (data.sort !== INSURANCE_DEFAULT_SORT) params.append("sort", data.sort);
  if (data.type !== "ALL") params.append("type", data.type);
  return params;
};

export const convertInsuranceSearchParamsToQueryKey = (
  data: InsuranceSearchParams,
) => [
  "search",
  data.search || "",
  "page",
  data.page,
  "pageSize",
  data.pageSize,
  "sort",
  data.sort,
  "type",
  data.type,
  "language",
  data.language,
];

export type InsuranceTypeCount = {
  type: InsuranceType;
  count: number;
};

/** One provider tile, shared by the server card and the client search item. */
export type InsuranceProviderCardItem = {
  slug: string;
  name: string;
  legalName: string | null;
  logoUrl: string | null;
  productCount: number;
  typeCount: number;
  /** Represented insurance types, most products first. */
  types: InsuranceTypeCount[];
  /** ISO timestamp of the reviewer's last check. */
  lastVerifiedAt: string;
  /** Product names that matched the search term, when there is one. */
  matchedProducts?: string[];
};

export type InsuranceProductPricingSummary = {
  basis: InsurancePricing["basis"];
  amount: number | null;
  currency: string;
  billingPeriod: NonNullable<InsurancePricing["billingPeriod"]> | null;
};

/** The compact product row rendered inside a provider's catalogue. */
export type InsuranceProductSummary = {
  slug: string;
  name: string;
  officialName: string;
  headline: string | null;
  summary: string | null;
  insuranceType: InsuranceType;
  productKind: InsuranceProductKind;
  policyYear: number | null;
  availability: InsuranceAvailability;
  pricing: InsuranceProductPricingSummary | null;
  officialUrl: string;
  coverageTotal: number;
  coverageIncluded: number;
  coverageOptional: number;
  coverageConditional: number;
  coverageExcluded: number;
  appRequired: boolean | null;
  hasQuoteUrl: boolean;
  affiliateStatus: InsuranceAffiliateStatus;
};

export type InsuranceProductGroup = {
  type: InsuranceType;
  products: InsuranceProductSummary[];
};

export type InsuranceRelatedProductLink = {
  slug: string;
  name: string;
  relationship: "BUNDLE_COMPONENT" | "AVAILABLE_WITH";
};

/** Everything the product drawer renders, served by the public product API. */
export type InsuranceProductDetailData = {
  provider: {
    slug: string;
    name: string;
  };
  slug: string;
  officialName: string;
  insuranceType: InsuranceType;
  productKind: InsuranceProductKind;
  productFamilyKey: string | null;
  policyYear: number | null;
  availability: InsuranceAvailability;
  underwriterName: string | null;
  officialUrl: string;
  quoteUrl: string | null;
  claimsUrl: string | null;
  affiliateUrl: string | null;
  affiliateStatus: InsuranceAffiliateStatus;
  applicationLanguages: string[] | null;
  policyLanguages: string[] | null;
  supportLanguages: string[] | null;
  applicationChannels: InsuranceChannel[] | null;
  claimsChannels: InsuranceChannel[] | null;
  appRequired: boolean | null;
  pricing: InsurancePricing | null;
  eligibility: InsuranceEligibility | null;
  coverage: InsuranceCoverageItem[];
  contract: InsuranceContract | null;
  facts: InsuranceFact[];
  sources: InsuranceSource[];
  lastVerifiedAt: string | null;
  related: InsuranceRelatedProductLink[];
  text: {
    name: string;
    headline: string;
    summary: string;
    introduction: string;
    coverageDetails: string;
    exclusionsDetails: string;
    pricingDetails: string;
    eligibilityDetails: string;
    householdDetails: string | null;
    rentingDetails: string | null;
    applicationDetails: string | null;
    claimsDetails: string;
    cancellationDetails: string;
    considerations: string[];
    factNotes: InsuranceFactNotes;
    coverageTexts: InsuranceCoverageText[];
    faqs: Faq[];
  };
};
